/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import { useTranslate } from '@im/react-localization';
import { ObjectMap } from 'mf_core/config';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { WarehouseDetailsModal } from './internal/WarehouseDetailsModal';
import { useHasPermission, useDecodedToken } from 'mf_core/auth';
import { WarehouseEMEAComponent } from './internal/WarehouseEMEAComponent';
import {
  EMEA_COUNTRY_CODES,
  EMEA_COUNTRY_CODES_PreOrderETA,
  Warehouse,
  BrazilPnaCallProps,
} from './internal/common.types';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import WarehouseVendorBHODirectShip from './internal/WarehouseVendorBHO_DS';
import { useFeatureFlag } from 'mf_core/feature-flag';
import { WarehouseETAComponent } from './WarehouseETA/WarehouseETAComponent';
import { WarehouseETAComponentEmea } from './WarehouseETA/WarehouseETAComponentEmea';
import SkeletonLoading from './WarehouseETA/internalETA/SkeletonLoading';
import WarehouseVendorBHODirectShipQty from '../WarehouseStockV2/internal/WarehouseVendorBHO_Qty';

type Props = {
  brazilReqProps?: BrazilPnaCallProps | undefined;
  //  dataatt?:DataAttributes;
  'data-displaysku'?: string;
  'data-engineid'?: string;
  'data-name'?: string;
  'data-planapsid'?: string;
  'data-promotionflagname'?: string;
  'data-recommendationrecordid'?: string;
  'data-testid'?: string;
  'data-vpn'?: string;
  discontinuedSku?: boolean;
  fromPage: string;
  fromPageForETA?: string;
  licenseSelectorVendorSubrange?: string;
  onclick?: ((sku: string) => void) | undefined;
  preOrderEtaStockDetails?: any;
  productType?: string;
  reserveFlag: boolean;
  reserveQuantityStock?: number;
  showWarehousePopupflag: boolean;
  sku: string;
  sortBy?: string;
  stockStatus: any;
  stockStyles?: ObjectMap;
  totalAvailableStock: number;
  vendorCode?: string;
  warehouseStockDetail: any;
  warehouseStyles?: ObjectMap;
  warehouses?: Warehouse[];
};

export function WarehouseStockComponent(props: Props) {
  const translate = useTranslate();
  const {
    brazilReqProps,
    fromPage,
    fromPageForETA,
    licenseSelectorVendorSubrange,
    onclick,
    preOrderEtaStockDetails,
    productType,
    reserveFlag,
    reserveQuantityStock,
    showWarehousePopupflag,
    sku,
    stockStatus,
    stockStyles,
    totalAvailableStock,
    vendorCode,
    warehouseStockDetail,
    warehouseStyles,
    warehouses,
  } = props;

  const [showWarehouseDetails, setShowWarehouseDetails] = useState(
    showWarehousePopupflag
  );

  const [warehouseStockDetailUpdate] = useState(warehouseStockDetail);
  const hasPermission = useHasPermission();
  const decodedTokenV1 = useDecodedToken();
  const siteCode = decodedTokenV1?.siteCode;

  const [stockStatusUpdate, setStockStatusUpdate] = useState(stockStatus);
  const { isReady, value: configKey } = useFeatureFlag([
    'X4C_StockStatus_InStock',
    'im-pricing-eta-applicablecountry-ui-isenabled',
    'X4C_EMEA_ShowCentralStockTtypeSKU',
  ]);

  const inStockKeyV1 = isReady
    ? configKey['X4C_StockStatus_InStock'] === 'true'
    : false;

  const etakeyV1 = isReady
    ? configKey['im-pricing-eta-applicablecountry-ui-isenabled'] === 'true'
    : false;

  const ShowCentralStockTtypeSkuV1 = isReady
    ? configKey['X4C_EMEA_ShowCentralStockTtypeSKU'] === 'true'
    : false;

  const [boKeyEtakey, setBoKeyEtakey] = useState(etakeyV1);

  const getStockText = () => {
    switch (stockStatusUpdate.toUpperCase()) {
      case 'STOCKABLE':
      case 'INSTOCK':
        return totalAvailableStock === 0
          ? translate('Cart.OutOfStock')
          : [
              inStockKeyV1
                ? translate('Cart.InStock')
                : [
                    stockStatusUpdate === 'STOCKABLE' &&
                    totalAvailableStock === 9999999
                      ? translate('Cart.Warranty')
                      : `${totalAvailableStock} ${translate('Cart.InStock')}`,
                  ],
            ];
      case 'DOWNLOADABLE':
        return translate('Cart.Download');
      case 'DIRECT_SHIP':
        return (
          <WarehouseVendorBHODirectShip
            fromPage={fromPage}
            stockStatus={stockStatusUpdate}
            warehouseStockDetail={warehouseStockDetail}
          ></WarehouseVendorBHODirectShip>
        );
      case 'LICENSE':
        return translate('Cart.License');
      case 'WARRANTY':
        return translate('Cart.Warranty');
      case 'SERVICE':
        return translate('Cart.Service');
    }
  };
  const { 'data-testid': testid = 'required' } = props;
  const { 'data-displaysku': displaysku = 'required' } = props;
  const { 'data-engineid': engineid = 'required' } = props;
  const { 'data-name': dataname = 'required' } = props;
  const { 'data-planapsid': planapsid = 'required' } = props;
  const { 'data-vpn': vpn = 'required' } = props;
  const { 'data-recommendationrecordid': recommendid = 'required' } = props;
  const { 'data-promotionflagname': promflag = 'required' } = props;

  const [showWarehouseOS, setShowWarehouseOS] = useState(true);

  useEffect(() => {
    if (siteCode?.toUpperCase() === 'IN') {
      if (
        (fromPage === 'product' || 'productdetails') &&
        stockStatusUpdate?.toUpperCase() === 'OUTOFSTOCK'
      ) {
        setShowWarehouseOS(false);
      } else if (
        fromPage === 'cart' &&
        stockStatusUpdate?.toUpperCase() === 'STOCKABLE' &&
        totalAvailableStock === 0
      ) {
        setShowWarehouseOS(false);
      }
    }
    if (
      totalAvailableStock !== 0 &&
      totalAvailableStock === Number(localStorage.getItem('vendorBohQuantity'))
    ) {
      fromPage === 'cart'
        ? setStockStatusUpdate('DIRECT_SHIP')
        : setStockStatusUpdate('DirectShip');
    }
  }, [
    siteCode,
    fromPage,
    showWarehouseOS,
    stockStatusUpdate,
    totalAvailableStock,
  ]);

  useEffect(() => {
    setStockStatusUpdate(stockStatus);
  }, [stockStatus]);

  useEffect(() => {
    if (onclick !== null && onclick !== undefined) onclick(sku);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      etakeyV1 === true &&
      (siteCode?.toUpperCase() === 'US' || siteCode?.toUpperCase() === 'CA')
    ) {
      setBoKeyEtakey(false);
    }
  }, [etakeyV1, siteCode]);

  const onClickSendDataForDigitalAnalytic = (sku: string) => {
    onclick && onclick(sku);
  };

  return (
    <>
      <WarehouseVendorBHODirectShipQty
        fromPage={fromPage}
        warehouseStockDetail={warehouseStockDetail}
      ></WarehouseVendorBHODirectShipQty>

      {isReady && boKeyEtakey === false ? (
        <>
          {EMEA_COUNTRY_CODES.includes(siteCode?.toUpperCase()) &&
          (stockStatusUpdate?.toUpperCase() === 'INSTOCK' ||
            stockStatusUpdate?.toUpperCase() === 'OUTOFSTOCK' ||
            stockStatusUpdate?.toUpperCase() === 'STOCKABLE') &&
          totalAvailableStock !== 9999999 &&
          warehouses ? (
            <>
              {hasPermission(['SeeResellerPriceAndStockOnCatalogue']) && (
                <WarehouseEMEAComponent
                  ShowCentralStockTtypeSku={ShowCentralStockTtypeSkuV1}
                  data-testid={sku}
                  fromPage={fromPage}
                  licenseSelectorVendorSubrange={licenseSelectorVendorSubrange}
                  onclick={onClickSendDataForDigitalAnalytic}
                  productType={productType}
                  reserveFlag={reserveFlag}
                  showWarehousePopupflag={showWarehousePopupflag}
                  siteCode={siteCode}
                  sku={sku}
                  skuType={productType}
                  stockStatus={stockStatusUpdate}
                  totalAvailableStock={totalAvailableStock}
                  vendorCode={vendorCode}
                  warehouseStockDetail={warehouseStockDetail}
                  warehouses={warehouses}
                ></WarehouseEMEAComponent>
              )}
            </>
          ) : (
            <>
              {hasPermission(['SeeResellerPriceAndStockOnCatalogue']) &&
                fromPage === 'cart' && (
                  <>
                    <Typography
                      bgcolor={
                        stockStatus === 'STOCKABLE' && totalAvailableStock === 0
                          ? '#F7E9E9'
                          : '#E3FFFA'
                      }
                      component="div"
                      data-displaysku={displaysku}
                      data-engineid={engineid}
                      data-name={dataname}
                      data-promotionflagname={promflag}
                      data-recommendationrecordid={recommendid}
                      p="6px 12px"
                      sx={{
                        borderRadius: '16px',
                        display: 'inline-flex',
                        flexDirection: 'row',
                        fontWeight: 'bold',
                        gap: '4px',
                        ...warehouseStyles,
                      }}
                      variant="body2"
                    >
                      {stockStatusUpdate === 'STOCKABLE' &&
                      totalAvailableStock !== 9999999 ? (
                        <>
                          <StyledButton
                            data-testid={`${testid}-WarehouseCartStock_${displaysku}${engineid}${dataname}${planapsid}${vpn}${recommendid}${props.sortBy}`}
                            onClick={() => {
                              onclick && onclick(sku);
                              if (siteCode?.toUpperCase() === 'IN') {
                                setShowWarehouseDetails(showWarehouseOS);
                              } else {
                                setShowWarehouseDetails(true);
                              }
                            }}
                            size="small"
                            sx={{
                              borderRadius: '0px',
                              color:
                                // stockStatus === `STOCKABLE` ? '#18846F' : '#B11616',
                                stockStatusUpdate === 'STOCKABLE' &&
                                totalAvailableStock === 0
                                  ? '#B11616'
                                  : '#054F31',
                              cursor:
                                stockStatus === 'STOCKABLE'
                                  ? 'pointer'
                                  : 'initial',
                              fontSize: '0.87rem',
                              fontWeight: 'bold',
                              lineHeight: '19px',
                              padding: '0px',
                              textTransform: 'none',
                              whiteSpace: 'break-spaces',
                            }}
                            variant="text"
                          >
                            <Stack
                              alignItems="center"
                              direction="row"
                              divider={
                                reserveFlag && (
                                  <Divider
                                    flexItem
                                    orientation="vertical"
                                    sx={{
                                      border: '1.2px solid #054F31',
                                      opacity: '1',
                                    }}
                                  />
                                )
                              }
                              justifyContent="center"
                              sx={{
                                '.MuiDivider-root': { borderColor: '#054F31' },
                                display: 'flex',
                                fontSize: '16px',
                                lineHeight: '18px',
                                ...(stockStyles ?? {}),
                              }}
                            >
                              <Box sx={{ display: 'flex' }}>
                                {getStockText()}
                                {'  '}
                              </Box>
                              {reserveFlag === true && (
                                <div style={{ marginLeft: '0.50rem' }}>
                                  <ReservedIcon component="span">
                                    R
                                  </ReservedIcon>
                                </div>
                              )}
                            </Stack>
                          </StyledButton>
                          {showWarehouseDetails &&
                            !(siteCode?.toUpperCase() === 'HK') && (
                              <WarehouseDetailsModal
                                brazilReqProps={brazilReqProps}
                                fromPage={fromPage}
                                id={testid}
                                licenseSelectorVendorSubrange={
                                  licenseSelectorVendorSubrange
                                }
                                onClose={() => setShowWarehouseDetails(false)}
                                open={showWarehouseDetails}
                                reserveFlag={reserveFlag}
                                sku={sku}
                                skuType={productType}
                                vendorCode={vendorCode}
                                warehouseDetails={
                                  warehouseStockDetailUpdate ?? []
                                }
                              />
                            )}
                        </>
                      ) : (
                        <Typography
                          color={
                            stockStatusUpdate === 'STOCKABLE' &&
                            totalAvailableStock === 0
                              ? '#B11616'
                              : '#054F31'
                          }
                          component="span"
                          sx={{
                            borderRadius: '25px',
                            cursor:
                              stockStatusUpdate === 'STOCKABLE'
                                ? 'pointer'
                                : 'initial',
                            fontSize: '0.87rem',
                            fontWeight: '600',
                            lineHeight: '19px',
                            padding: '0px',
                            ...warehouseStyles,
                          }}
                          variant="body2"
                        >
                          {getStockText()}
                        </Typography>
                      )}
                    </Typography>
                  </>
                )}

              {hasPermission(['SeeResellerPriceAndStockOnCatalogue']) &&
                (fromPage === 'product' || fromPage === 'productdetails') && (
                  <>
                    <Typography
                      bgcolor={
                        stockStatusUpdate === 'Outofstock' ||
                        stockStatusUpdate === 'Out of stock'
                          ? '#F7E9E9'
                          : '#E3FFFA'
                      }
                      component="div"
                      data-displaysku={displaysku}
                      data-engineid={engineid}
                      data-name={dataname}
                      //data-planapsid={planapsid}
                      data-promotionflagname={promflag}
                      data-recommendationrecordid={recommendid}
                      p="6px 12px"
                      sx={{
                        borderRadius: '16px',
                        display: 'inline-flex',
                        flexDirection: 'row',
                        fontWeight: 'bold',
                        gap: '4px',
                        ...warehouseStyles,
                      }}
                      variant="body2"
                    >
                      {(totalAvailableStock > 0 &&
                        totalAvailableStock !== 9999999 &&
                        stockStatusUpdate === 'Instock') ||
                      stockStatusUpdate === 'Outofstock' ? (
                        <>
                          <StyledButton
                            data-testid={`${testid}-WarehouseOldStock`}
                            onClick={() => {
                              onclick && onclick(sku);
                              if (siteCode?.toUpperCase() === 'IN') {
                                setShowWarehouseDetails(showWarehouseOS);
                              } else {
                                setShowWarehouseDetails(true);
                              }
                            }}
                            size="small"
                            sx={{
                              borderRadius: '0px',
                              color:
                                stockStatusUpdate === 'Instock'
                                  ? '#054F31'
                                  : '#B11616',
                              cursor:
                                stockStatusUpdate === 'Instock'
                                  ? 'pointer'
                                  : 'initial',
                              fontSize:
                                fromPage === 'productdetails'
                                  ? '0.87rem'
                                  : '12px',
                              fontWeight: 'bold',
                              lineHeight: '19px',
                              padding: '0px',
                              textTransform: 'none',
                              whiteSpace: 'break-spaces',
                            }}
                            variant="text"
                          >
                            <Stack
                              alignItems="center"
                              direction="row"
                              divider={
                                reserveFlag && (
                                  <Divider
                                    flexItem
                                    orientation="vertical"
                                    sx={{
                                      border: '1.2px solid #054F31',
                                      opacity: '1',
                                    }}
                                  />
                                )
                              }
                              justifyContent="center"
                              sx={{
                                '.MuiDivider-root': { borderColor: '#054F31' },
                                display: 'flex',
                                fontSize: '16px',
                                lineHeight: '18px',
                                ...(stockStyles ?? {}),
                              }}
                            >
                              <Box
                                sx={{
                                  cursor: 'pointer',
                                  display: 'flex',
                                }}
                              >
                                {stockStatusUpdate === 'Instock' &&
                                !inStockKeyV1
                                  ? totalAvailableStock
                                  : ''}{' '}
                                {translate(`Common.${stockStatusUpdate}`)}
                                {'  '}
                              </Box>
                              {reserveFlag === true && (
                                <div
                                  className="reserved-icon"
                                  style={{ marginLeft: '0.50rem' }}
                                >
                                  <ReservedIcon component="span">
                                    R
                                  </ReservedIcon>
                                </div>
                              )}
                            </Stack>
                          </StyledButton>

                          {showWarehouseDetails &&
                            !(siteCode?.toUpperCase() === 'HK') && (
                              <WarehouseDetailsModal
                                brazilReqProps={brazilReqProps}
                                fromPage={fromPage}
                                id={`${testid}-WarehouseDetailsModal`}
                                licenseSelectorVendorSubrange={
                                  licenseSelectorVendorSubrange
                                }
                                onClose={() => setShowWarehouseDetails(false)}
                                open={showWarehouseDetails}
                                reserveFlag={reserveFlag}
                                sku={sku}
                                skuType={productType}
                                vendorCode={vendorCode}
                                warehouseDetails={[]}
                              />
                            )}
                        </>
                      ) : (
                        [
                          stockStatusUpdate !== 'DirectShip' ? (
                            <Typography
                              color={
                                stockStatusUpdate === 'Outofstock'
                                  ? '#B11616'
                                  : '#054F31'
                              }
                              component="span"
                              sx={{
                                borderRadius: '25px',
                                display: 'inline-flex',
                                fontSize: '16px',
                                fontWeight: '600',
                                lineHeight: '17px',
                                padding: '0px',
                                ...warehouseStyles,
                                ...(stockStyles ?? {}),
                              }}
                              variant="body2"
                            >
                              {totalAvailableStock === 9999999 &&
                              stockStatusUpdate === 'Instock'
                                ? translate('Common.Available')
                                : translate(`Common.${stockStatusUpdate}`)}
                            </Typography>
                          ) : (
                            <WarehouseVendorBHODirectShip
                              fromPage={fromPage}
                              stockStatus={stockStatusUpdate}
                              warehouseStockDetail={warehouseStockDetail}
                            ></WarehouseVendorBHODirectShip>
                          ),
                        ]
                      )}
                    </Typography>
                  </>
                )}
            </>
          )}
        </>
      ) : (
        <>
          {isReady && boKeyEtakey === true ? (
            <>
              {!EMEA_COUNTRY_CODES_PreOrderETA.includes(
                siteCode?.toUpperCase()
              ) ? (
                // DE,UK country Called
                <WarehouseETAComponent
                  data-testid={sku}
                  fromPage={fromPage}
                  fromPageForETA={fromPageForETA ?? null}
                  licenseSelectorVendorSubrange={licenseSelectorVendorSubrange}
                  preOrderEtaStockDetails={preOrderEtaStockDetails}
                  reserveFlag={reserveFlag}
                  reserveQuantityStock={reserveQuantityStock ?? 0}
                  showWarehousePopupflag={showWarehousePopupflag}
                  siteCode={siteCode}
                  sku={sku}
                  skuType={productType}
                  stockStatus={stockStatusUpdate}
                  totalAvailableStock={totalAvailableStock}
                  vendorCode={vendorCode}
                  warehouseStockDetail={warehouseStockDetail}
                  warehouses={warehouses}
                ></WarehouseETAComponent>
              ) : (
                // ES,IT,FR country Called
                <WarehouseETAComponentEmea
                  data-testid={sku}
                  fromPage={fromPage}
                  fromPageForETA={fromPageForETA ?? null}
                  licenseSelectorVendorSubrange={licenseSelectorVendorSubrange}
                  preOrderEtaStockDetails={preOrderEtaStockDetails}
                  reserveFlag={reserveFlag}
                  reserveQuantityStock={reserveQuantityStock ?? 0}
                  showWarehousePopupflag={showWarehousePopupflag}
                  siteCode={siteCode}
                  sku={sku}
                  skuType={productType}
                  stockStatus={stockStatusUpdate}
                  totalAvailableStock={totalAvailableStock}
                  vendorCode={vendorCode}
                  warehouseStockDetail={warehouseStockDetail}
                  warehouses={warehouses}
                ></WarehouseETAComponentEmea>
              )}
            </>
          ) : (
            !isReady && <SkeletonLoading widthSkeleton={220}></SkeletonLoading>
          )}
        </>
      )}
    </>
  );
}

const StyledButton = styled(Button)({
  '&.MuiButton-root:focus': {
    outline: 'none',
  },
  '&.MuiButton-root:hover': {
    background: 'none',
  },
  '&.MuiButtonBase-root, &.MuiButton-root': {
    height: 'auto',
    textTransform: 'none',
  },
});

const ReservedIcon = styled(Box)(() => ({
  alignItems: 'center',
  border: '2px solid #054F31',
  color: '#054F31',
  display: 'inline-flex',
  height: 22,
  justifyContent: 'center',
  paddingRight: '0%',
  width: 20,
}));
